import {
  useEffect,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import { Stack } from '@chakra-ui/react'
import {
  authSelectors,
  getAdminNotifications,
  getEventMarkers,
  getGuestNotification,
  getUserProfile,
  handleLocationMyPosition,
  handleModalTitle,
  logOut,
  setBlogParent,
  setIsProfileActive,
  setOnPostEditor,
  setPosts,
  setPostsTypeStatus,
  setSelectedUserProfile,
  setUserEdited,
  setUserSectionPage,
  usersSelectors
} from 'store'
import {
  AnimatePresence,
  motion
} from 'framer-motion'
import {
  MY_PROFILES,
  PENDING,
  SETTINGS,
  SINGLE_PROFILE,
  SUCCEEDED,
  VERIFY_PAGE
} from 'utils/constants'
import { COLORS } from 'utils/styles'

import { ProfileBlog } from 'components/UserSection/ProfileBlog'
import { EditProfile } from 'components/UserSection/EditProfile'
import { MyProfiles } from 'components/UserSection/MyProfiles'
import { Settings } from 'components/UserSection/Settings'
import { SingleProfile } from 'components/UserSection/SingleProfile'

const MotionStack = motion(Stack)

export const UserSection = () => {
  const {
    croppedPicture,
    deleteProfileStatus,
    deleteUserStatus,
    getUserProfileStatus,
    isSettingPage,
    repostThisPostData,
    searchBar,
    searchSocialLinks,
    searchSocialLinksCategories,
    selectProfileStatus,
    updateUserProfileStatus,
    updateUserSettingsStatus,
    user,
    userEdited,
    userPosition,
    userProfile,
    userSectionPage
  } = useSelector(usersSelectors)

  const { modalTitle } = useSelector(authSelectors)

  const [selectedUser, setSelectedUser] = useState('')
  const [hideIt, setHideIt] = useState(false)

  const dispatch = useDispatch()

  const handleSelectUser = (item) => {
    setSelectedUser(item)
    dispatch(setUserSectionPage(SINGLE_PROFILE))

    if (item.full_name) {
      dispatch(setIsProfileActive(true))
    } else {
      dispatch(handleModalTitle(VERIFY_PAGE.EditProfile))
      dispatch(setIsProfileActive(false))
    }
  }

  const handleSelectLocation = (item) => {
    setSelectedUser(item)
    dispatch(setSelectedUserProfile(item))
    dispatch(handleLocationMyPosition(item))
  }

  const handleLogOut = () => {
    localStorage.setItem('recentSearches', '[]')
    dispatch(logOut(true))
    dispatch(getGuestNotification())
    document.querySelector('#chakra-modal--body-user-drawer').scrollTop = 0
  }

  const selectPage = () => {
    switch (userSectionPage) {
    case MY_PROFILES:
      return (
        <MyProfiles
          userProfile={userProfile}
          isLoading={getUserProfileStatus === PENDING || selectProfileStatus === PENDING}
          handleSelectUser={handleSelectUser}
          handleLogOut={handleLogOut}
        />
      )
    case SINGLE_PROFILE:
      return (
        <>
          <SingleProfile
            selectedUser={selectedUser}
            setUserSectionPage={setUserSectionPage}
            handleSelectLocation={handleSelectLocation}
            hideIt={hideIt}
            setHideIt={setHideIt}
            updateUserProfileStatus={updateUserProfileStatus}
          />

          <AnimatePresence
            mode="popLayout"
            onExitComplete={() => setHideIt(false)}
          >
            {
              modalTitle === VERIFY_PAGE.ProfileBlog && (
                <MotionStack
                  key="profile-blog"
                  initial={{ x: -450 }}
                  animate={{ x: 0 }}
                  exit={{ x: -450 }}
                  transition={{
                    type: 'tween',
                    duration: 0.8
                  }}
                >
                  <Stack
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100vh"
                    borderRadius="md"
                    backgroundColor={COLORS.white}
                    zIndex={-2}
                  />
                  <ProfileBlog
                    selectedUser={selectedUser}
                    affinityList={searchBar.affinities}
                  />
                </MotionStack>
              )
            }

            {
              modalTitle === VERIFY_PAGE.EditProfile && (
                <MotionStack
                  key="profile-edit"
                  initial={{ x: -450 }}
                  animate={{ x: 0 }}
                  exit={{ x: -450 }}
                  transition={{
                    type: 'tween',
                    duration: 0.8
                  }}
                >
                  <Stack
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100vh"
                    borderRadius="md"
                    backgroundColor={COLORS.white}
                    zIndex={-2}
                  />
                  <EditProfile
                    userPosition={userPosition}
                    isLoading={updateUserProfileStatus === PENDING}
                    deleteProfileStatus={deleteProfileStatus}
                    searchSocialLinks={searchSocialLinks}
                    searchSocialLinksCategories={searchSocialLinksCategories}
                    selectedUser={selectedUser}
                    setUserSectionPage={setUserSectionPage}
                    croppedPicture={croppedPicture}
                    affinityList={searchBar.affinities}
                    updateUserProfileStatus={updateUserProfileStatus}
                  />
                </MotionStack>
              )
            }
          </AnimatePresence>
        </>
      )
    case SETTINGS:
      return (
        <Settings
          isLoading={updateUserSettingsStatus === PENDING}
          user={user}
          deleteUserStatus={deleteUserStatus}
        />
      )
    default:
      return null
    }
  }

  useEffect(() => {
    if (updateUserProfileStatus === SUCCEEDED) {
      dispatch(getAdminNotifications())
      dispatch(getEventMarkers({ isAuth: true }))

      if (isSettingPage === true) {
        dispatch(setUserSectionPage(SETTINGS))
        dispatch(getUserProfile())
      } else if (userProfile) {
        dispatch(getUserProfile())

        userProfile.map((item) => {
          if (userEdited.id === item.id) {
            handleSelectUser(item)
            dispatch(setUserEdited([]))
          }

          return null
        })
      }
    }
  }, [updateUserProfileStatus])

  useEffect(() => {
    if (userSectionPage === SINGLE_PROFILE) {
      setHideIt(false)
    }
  }, [userSectionPage])

  useEffect(() => {
    if (Object.keys(repostThisPostData).length) {
      const activeProfile = userProfile.find((item) => item.is_active)

      if (activeProfile) {
        setSelectedUser(activeProfile)
        dispatch(setPostsTypeStatus('posted'))
        dispatch(setPosts([]))
        dispatch(setBlogParent([]))
        dispatch(setOnPostEditor(''))

        setTimeout(() => {
          dispatch(setUserSectionPage(SINGLE_PROFILE))

          setTimeout(() => {
            setHideIt(true)
            dispatch(handleModalTitle(VERIFY_PAGE.ProfileBlog))
          }, 200)
        }, 200)
      }
    } else {
      setSelectedUser('')
      dispatch(setUserSectionPage(MY_PROFILES))
    }
  }, [])

  return selectPage()
}
